import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import AnimatedSection from '../components/AnimatedSection';
import HeroBackground from '../components/HeroBackground';
import { siteInfo } from '../data/siteInfo';

const IconHouse = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path d="M8 22.5L24 9l16 13.5V38a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V22.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M18 40V26h12v14" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const IconHandshake = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path
      d="M6 22l8-5 5 3.5L28 14l14 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 26.5l5.5 5.5c2.2 2.2 5.8 2.2 8 0l2.5-2.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M26 29.5l2.8 2.8c1.8 1.8 4.7 1.8 6.5 0L40 28"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 20v8M38 21v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconKey = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="18" cy="22" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M23 25l17 10v4h-5v-3h-3v-3h-3l-3-2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const IconPeople = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="14" r="5" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="18" r="4" stroke="currentColor" strokeWidth="2" />
    <circle cx="36" cy="18" r="4" stroke="currentColor" strokeWidth="2" />
    <path d="M8 36c0-4 3.5-7 8-7h2M30 29h2c4.5 0 8 3 8 7M16 29c3.5 0 7 2.5 8 6 1-3.5 4.5-6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconGrowth = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path d="M10 36V28M20 36V22M30 36V18M40 36V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 14l12 8 8-6 12-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M34 8h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FOCUS_ICONS = [IconHouse, IconHandshake, IconKey, IconPeople, IconGrowth];

const Home = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.companyName,
    description: siteInfo.shortDescription,
    email: siteInfo.contact.email,
    ...(siteInfo.contact.phone && { telephone: siteInfo.contact.phone }),
    ...(siteInfo.contact.fullAddress && {
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteInfo.contact.addressLine1 || undefined,
        addressLocality: siteInfo.contact.city || 'Preston',
        postalCode: siteInfo.contact.postcode || undefined,
        addressCountry: 'GB'
      }
    })
  };

  return (
    <>
      <SEO
        title={`${siteInfo.companyName} | Property Investment in Preston`}
        description={siteInfo.shortDescription}
        schema={schema}
      />

      <section className="home-hero">
        <HeroBackground />

        <div className="home-hero__content">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="home-hero__badge"
            style={{ marginBottom: '18px' }}
          >
            {siteInfo.locationLabel}
          </motion.p>

          <motion.h1
            className="hero-h1 home-hero__title"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            {siteInfo.tagline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="home-hero__sub"
          >
            {siteInfo.companyName} is a UK-based property investment company focused on creating
            long-term value through smart investments, strategic management, and strong partnerships.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Link to="/about" className="btn-primary">Learn More</Link>
          </motion.div>
        </div>
      </section>

      <section className="home-focus-bar" aria-label="Our focus">
        <div className="container">
          <div className="home-focus-grid">
            {siteInfo.heroFocus.map((item, i) => {
              const Icon = FOCUS_ICONS[i % FOCUS_ICONS.length];
              return (
                <AnimatedSection key={item.title} variant="fadeUp" delay={i * 0.06}>
                  <div className="home-focus-item">
                    <div className="home-focus-item__icon">
                      <Icon />
                    </div>
                    <h3 className="home-focus-item__title">{item.title}</h3>
                    <p className="home-focus-item__text">{item.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <AnimatedSection variant="fadeUp" className="section-intro section-intro--center">
            <p className="eyebrow-label">Who We Are</p>
            <h2 className="section-heading">Property Investment, Done Simply</h2>
            <div className="divider divider-center" />
          </AnimatedSection>
          <AnimatedSection variant="fadeUp" delay={0.1}>
            <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center', color: '#4a5560', lineHeight: 1.9, fontSize: '1.05rem' }}>
              <p style={{ marginBottom: '1.25rem' }}>{siteInfo.longDescription}</p>
              <p style={{ marginBottom: '1.25rem' }}>{siteInfo.aboutExtra}</p>
              <p>{siteInfo.approachStatement}</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--offwhite)' }}>
        <div className="container">
          <AnimatedSection variant="fadeUp" className="section-intro section-intro--center">
            <p className="eyebrow-label">What We Do</p>
            <h2 className="section-heading">Our Focus</h2>
            <div className="divider divider-center" />
          </AnimatedSection>

          <div style={{ maxWidth: '720px', margin: '0 auto', display: 'grid', gap: '12px' }}>
            {siteInfo.focusAreas.map((item, i) => (
              <AnimatedSection key={item} variant="fadeUp" delay={i * 0.05}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    padding: '16px 20px',
                    background: '#fff',
                    border: '1px solid rgba(11, 28, 44, 0.08)'
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: '8px',
                      height: '8px',
                      marginTop: '8px',
                      flexShrink: 0,
                      background: 'var(--gold)',
                      borderRadius: '50%'
                    }}
                  />
                  <p style={{ color: '#2a3340', fontSize: '1.02rem', lineHeight: 1.6, margin: 0 }}>{item}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section
        className="home-cta-banner"
        style={{
          background: 'var(--navy-deep)',
          padding: 'clamp(56px, 10vw, 96px) clamp(16px, 4vw, 24px)',
          textAlign: 'center'
        }}
      >
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <AnimatedSection variant="fadeUp">
            <p style={{ color: 'var(--gold)', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {siteInfo.closingLine}
            </p>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: 800, color: '#fff', lineHeight: 1.25, marginBottom: '20px' }}>
              Looking to invest, buy or sell?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '36px' }}>
              {siteInfo.belief}
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary">Get in Touch</Link>
              <Link to="/services" className="btn-secondary">Our Services</Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default Home;
